import {isNil, isNotNil} from 'ramda';

import type {ConfiguratorBundleResponseBody} from '@dms/api/new-vehicle';
import i18n from '@dms/i18n';

import {getExclusionConflict} from './getExclusionConflict';
import type {WheelSet} from './getWheelSet';

const t = (key: string, defaultValue: string) => i18n.t(key, {defaultValue});

type ExclusionCheckInput = {
  bundle: ConfiguratorBundleResponseBody;
  variantId: string;
  selectedPackageIds: string[];
  autoAddedIds: ReadonlySet<string>;
  factoryFreezeReason?: string;
  wheelSet: WheelSet;
};

// Every way into the build (feature, package, package-only and resolution actions) asks this one
// question, against the candidate selection. The locked wheel stays, and after the spec freeze so
// do factory features and packages (see `toggle` in OptionsPackages).
export function getExclusionCheck(input: ExclusionCheckInput) {
  const {bundle, wheelSet, factoryFreezeReason} = input;
  // ponytail: a backend that predates exclusion rules (D71) omits the field (or sends null) during
  // rollout; drop the fallback once every environment serves it.
  const exclusions = isNotNil(bundle.featureExclusions) ? bundle.featureExclusions : [];
  const featureName = (id: string) =>
    bundle.features.find((feature) => feature.id === id)?.name ?? id;

  return (addedFeatureIds: string[]) => {
    const conflict = getExclusionConflict(addedFeatureIds, {
      features: bundle.features,
      packages: bundle.packages,
      exclusions,
      variantId: input.variantId,
      featureIds: wheelSet.getCandidateFeatureIds(addedFeatureIds),
      packageIds: input.selectedPackageIds,
      autoAddedIds: input.autoAddedIds,
      isPinned: (id) =>
        id === wheelSet.wheelLock?.id ||
        (isNotNil(factoryFreezeReason) &&
          bundle.features.find((feature) => feature.id === id)?.fitStage === 'factory'),
      canRemovePackages: isNil(factoryFreezeReason),
    });
    // A partner that comes through a package names the package that leaves with it.
    const names = conflict.partners.map(({id, packageIds}) =>
      packageIds.length > 0
        ? `${featureName(id)} (${packageIds
            .map((packageId) => bundle.packages.find((item) => item.id === packageId)?.name)
            .join(', ')})`
        : featureName(id)
    );
    return {
      ...conflict,
      reason:
        names.length > 0
          ? `${t('entity.newVehicle.labels.notWith', 'Not with')}: ${names.join(', ')}`
          : undefined,
    };
  };
}
